import React, { useEffect, useState, useContext } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  KeyboardAvoidingView,
  Alert} from "react-native";
import { Platform } from "react-native";
import SignInStyle from "./SignInPageStyle";

import { ApiService, setAuthToken } from "../../service/ApiService";

import { ApiResponse } from "../../types/ApiResponse";
import { Prompt } from "expo-auth-session";
import * as Google from "expo-auth-session/providers/google";
import * as WebBrowser from "expo-web-browser";
import {
  GoogleSignin,
  GoogleSigninButton,
  isErrorWithCode,
  isSuccessResponse,
  statusCodes} from "@react-native-google-signin/google-signin";
import OverlayLoader from "../../components/LoadingOverlay";
import Constants from "expo-constants";
import { AuthContext } from "../../contexts/AuthContext";

import * as jwtDecode from "jwt-decode";
import commonStyles from "../../styles/commonStyles";
import Toast from "react-native-toast-message";
import { ScrollView, Switch } from "react-native-gesture-handler";
import PropertiesStyle from "../Properties/PropertiesStyle";
import {
  NavigationProp,
  useNavigation,
  useRoute} from "@react-navigation/native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import AppleAuth from "../../components/AppleAuth";
import OverflowTooltipText from "../../components/OverflowTooltipText";
import EmailPromptModal from "../../components/EmailPromptModal";
import { randomUUID } from "crypto";
import { SafeAreaView } from "react-native-safe-area-context";
import { fonts } from "../../styles/theme";
import AppText from "../../components/AppText";
import AppTextInput from "../../components/AppTextInput";

WebBrowser.maybeCompleteAuthSession();
const webClientId = Constants.expoConfig?.extra?.WEB_ID;
const iosClientId = Constants.expoConfig?.extra?.IOS_ID;
const androidClientId = Constants.expoConfig?.extra?.ANDROID_ID;

type FormData = {
  email: string;
  password: string;
};
const SignInPage: React.FC = () => {
  GoogleSignin.configure({
    webClientId: webClientId,
    offlineAccess: true,
    forceCodeForRefreshToken: true,
    iosClientId: iosClientId});
  const navigation = useNavigation<NavigationProp<any>>();
  const [isGoogleAuth, setIsGoogleAuth] = useState(false);
  const toggleSwitch = () => setIsGoogleAuth((previousState) => !previousState);
  const { setUserToken } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<any>({
    email: "",
    password: ""});
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [pendingAppleData, setPendingAppleData] = useState<any>(null);

  const [request, response, promptAsync] = Google.useAuthRequest({
    iosClientId: iosClientId,
    androidClientId: androidClientId,
    webClientId: webClientId,
    prompt: "select_account" as Prompt,
    responseType: "id_token",
    scopes: ["profile", "email"]});

  useEffect(() => {
    console.log("Google Auth Response:>>>>>>>>>", response);
    if (response?.type === "success" && "params" in response) {
      const token = response?.params?.id_token;
      let email: string | undefined;
      if (token) {
        decodeToken(token);
        try {
          email = (jwtDecode.jwtDecode(token) as any)?.email;
        } catch (_) {}
      }
      sendTokenToApi(token, email);
    }
  }, [response]);

  const sendTokenToApi = async (token: string, email?: string) => {
    try {
      console.log("Sending Google token to API>>>>>:", token);
      setIsLoading(true);
      const apiResponse: ApiResponse = await ApiService.post<{
        isSuccess: boolean;
        data: any;
      }>("portal/auth/login", { idToken: token });

      console.log("API Response:", JSON.stringify(apiResponse));

      if (apiResponse.isSuccess) {
        const data = apiResponse.data || {};
        const partner = data.partner || null;
        const tokenFromApi = data.token || "";

        if (!tokenFromApi) {
          setIsLoading(false);
          await showAccountNotFound(apiResponse.responseMessage, email);
          return;
        }

        // store partner/info safely
        try {
          if (partner) {
            await AsyncStorage.setItem("apiResponse", JSON.stringify(partner));
            await AsyncStorage.setItem("user", JSON.stringify(partner));
            // ensure userId is always a string
            if (partner.id !== undefined && partner.id !== null) {
              await AsyncStorage.setItem("userId", String(partner.id));
            }
            if (partner.picture || partner.photo) {
              await AsyncStorage.setItem(
                "setUserProfile",
                partner.picture || partner.photo
              );
            }
          } else {
            // fallback: store whatever data the API returned
            await AsyncStorage.setItem("apiResponse", JSON.stringify(data));
            await AsyncStorage.setItem("user", JSON.stringify(data));
          }

          if (tokenFromApi) {
            await AsyncStorage.setItem("userToken", tokenFromApi);
          }
        } catch (storageError) {
          console.error("AsyncStorage error while saving API response:", storageError);
        }

        setAuthToken();
        if (tokenFromApi) {
          setUserToken(tokenFromApi);
        }
        setIsLoading(false);
      } else {
        setIsLoading(false);
        await showAccountNotFound(
          apiResponse.responseMessage || apiResponse.error?.message,
          email
        );
      }
    } catch (error: any) {
      console.error("Error sending token to API:", error);
      setIsLoading(false);
      if (error?.response) {
        const data = error.response.data || {};
        await showAccountNotFound(
          data.responseMessage || data.error?.message || data.message,
          email
        );
        return;
      }

      Toast.show({
        type: "error",
        text1: "Login",
        text2: `${error}`});
    }
  };

  const showAccountNotFound = async (message?: string, email?: string) => {
    try {
      await GoogleSignin.signOut();
    } catch (_) {
    }
    navigation.navigate("AccountNotFound", { message, email });
  };

  // Helper: Trigger the modal instead of Alert.prompt
  const openEmailPrompt = (
    appleId: string,
    email: string,
    identityToken: string,
    firstName: string,
    lastName: string
  ) => {
    setPendingAppleData({
      appleId,
      email,
      identityToken,
      firstName: firstName,
      lastName: lastName});
    setEmailModalVisible(true);
  };

  // When user submits email:
  // const handleEmailSubmit = ( finalEmail:string ) => {
  //   setEmailModalVisible(false);

  //   const { appleId, identityToken, firstName, lastName } = pendingAppleData;

  //   sendAppleTokenToApi(
  //     appleId,
  //     finalEmail,
  //     identityToken,
  //     firstName,
  //     lastName
  //   );
  // };

  const handleEmailSubmit = ({
    firstName,
    lastName}: {
    firstName: string;
    lastName: string;
  }) => {
    setEmailModalVisible(false);

    const { appleId, identityToken, email } = pendingAppleData || {};

    const generateRandomString = (length = 5) => {
      const timestamp = Date.now().toString();
      const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
      let suffix = "";
      for (let i = 0; i < 3; i++) {
        suffix += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      // returns timestamp + 3 random chars (length param kept for compatibility)
      return `${timestamp}${suffix}`;
    };

    let generatedEmail = email;
    if (!email || email === "null") {
      const rand = generateRandomString(5);
      generatedEmail = `epicestates.${rand}@privaterelay.appleid.com`;
    }

    sendAppleTokenToApi(
      appleId,
      generatedEmail,
      identityToken,
      firstName,
      lastName
    );
  };

  const sendAppleTokenToApi = async (
    appleId: string,
    email: string,
    identityToken: string,
    firstName: string,
    lastName: string
  ) => {
    try {
      console.log("Sending Apple token to API with:", {
        appleId,
        email,
        identityToken,
        firstName,
        lastName});

      setIsLoading(true);
      // if(email == 'null'){
      //   console.warn("Email is 'null', cannot proceed.");
      //   openEmailPrompt(appleId, '', identityToken, firstName, lastName);
      //   return;
      // }

      const apiResponse: ApiResponse = await ApiService.post<{
        isSuccess: boolean;
      }>("portal/auth/login-with-apple", {
        appleId,
        email,
        identityToken,
        firstName,
        lastName});


      if (apiResponse.isSuccess) {
        await AsyncStorage.setItem(
          "apiResponse",
          JSON.stringify(apiResponse.data.partner)
        );
        await AsyncStorage.setItem(
          "user",
          JSON.stringify(apiResponse.data.partner)
        );
        await AsyncStorage.setItem("userToken", apiResponse.data.token);
        await AsyncStorage.setItem(
          "userId",
          apiResponse.data.partner.id.toString()
        );

        setAuthToken();
        setUserToken(apiResponse.data.token);
      }
    } catch (error: any) {
      console.log("the error????", error);

      const message = error?.response?.data?.responseMessage || error?.message;

      console.error("the sign in failed", message);

      // REQUIRED BY BACKEND — show email prompt
      if (message === "Email is required for first-time Apple sign in") {
        openEmailPrompt(appleId, email, identityToken, firstName, lastName);
        return; // stop here, modal will continue
      }
    } finally {
      setIsLoading(false);
    }
  };

  const decodeToken = async (token: string) => {
    const decodedToken = jwtDecode.jwtDecode(token);
    await storeUserInfo(JSON.stringify(decodedToken));
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const signIn = async () => {
    try {
      console.log("Initiating Google Sign-In...");
      await GoogleSignin.hasPlayServices();
      const response = await GoogleSignin.signIn();
      if (isSuccessResponse(response)) {
        // const idToken = response?.data?.idToken;

        const idToken = response?.data?.idToken;
        if (idToken) {
          await storeUserInfo(JSON.stringify(response.data.user));
          sendTokenToApi(idToken, response.data.user?.email);
        }
      } else {
        console.log("the ererer ");
      }
    } catch (error) {
      console.error("Google Sign-In error:", error);
      Toast.show({
        type: "error",
        text1: "Login",
        text2: `${error}`});
      if (isErrorWithCode(error)) {
        switch (error.code) {
          case statusCodes.IN_PROGRESS:
            break;
          case statusCodes.PLAY_SERVICES_NOT_AVAILABLE:
            break;
          default:
        }
      } else {
      }
    }
  };

  const storeUserInfo = async (userData: any) => {
    const data = typeof userData === "string" ? JSON.parse(userData) : userData;
    const profilePicture = data.picture || data.photo;
    if (profilePicture) {
      await AsyncStorage.setItem("setUserProfile", profilePicture);
    }
  };

  const onLogin = async () => {
    if (formData.email && formData.password) {
      setIsLoading(true);
      try {
        const apiResponse: ApiResponse = await ApiService.post<{
          isSuccess: boolean;
          data: { token: string };
        }>("portal/auth/login", {
          email: formData.email,
          password: formData.password});

        if (apiResponse.isSuccess) {
          await AsyncStorage.setItem(
            "apiResponse",
            JSON.stringify(apiResponse.data.partner)
          );
          await AsyncStorage.setItem("setUserProfile", "local-defulat");
          await AsyncStorage.setItem("userToken", apiResponse.data.token);
          await AsyncStorage.setItem(
            "user",
            JSON.stringify(apiResponse.data.partner)
          );
          await AsyncStorage.setItem(
            "userId",
            apiResponse.data.partner.id.toString()
          );
          setAuthToken();
          setUserToken(apiResponse.data.token);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        Toast.show({
          type: "error",
          text1: "Login",
          text2: `${error}`});
      }
    } else {
      Toast.show({
        type: "error",
        text1: "Please enter email and password"});
    }
  };

  const handleAppleCredential = (credential: any) => {
    console.log("Apple Credential received>>>>>>>>>>>>.:", credential);
    const identityToken = credential.identityToken;
    if (identityToken) {
      const appleId = credential.user || credential.appleId;
      const email = credential.email || "";
      const firstName = credential.fullName?.givenName || "";
      const lastName = credential.fullName?.familyName || "";
      sendAppleTokenToApi(appleId, email, identityToken, firstName, lastName);
    }
  };

  const route: any = useRoute();
  const [processedAppleData, setProcessedAppleData] = useState(false);

  const mapExchangeToCredential = (appleData: any) => {
    if (!appleData) return null;
    return {
      identityToken: appleData.identityToken || "",
      appleId: appleData.appleId,
      email: appleData.email || "",
      firstName: appleData.fullName?.givenName || "",
      lastName: appleData.fullName?.familyName || ""};
  };

  useEffect(() => {
    const appleData = route?.params?.appleData;
    if (!appleData || processedAppleData) return;

    const credential = mapExchangeToCredential(appleData);
    if (credential?.identityToken) {
      handleAppleCredential(credential);
      setProcessedAppleData(true);
      // try to clear the param so it won't be reprocessed on navigation
      try {
        (navigation as any).setParams({ appleData: undefined });
      } catch (_) {
        // ignore if not supported
      }
    } else {
      console.warn(
        "SignInPage: appleData missing identityToken, skipping login."
      );
    }
  }, [route?.params, processedAppleData]);

  return (
    <SafeAreaView style={[commonStyles.flex1]}>
      <OverlayLoader visible={isLoading}></OverlayLoader>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
      >
        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
        >
          <View style={SignInStyle.container}>
            <View style={SignInStyle.header}>
              <View style={SignInStyle.imageContainer}>
                <Image
                  source={require("../../../assets/adaptive-icon.png")}
                  style={{ width: 350, height: 100 }}
                />
                <AppText style={[SignInStyle.subHeaderText, commonStyles.mt5]}>
                  #iamtheowner
                </AppText>
              </View>
            </View>
            <View style={SignInStyle.content}>
              <View
                style={[
                  PropertiesStyle.switchContainer,
                  { justifyContent: "flex-end", marginTop: 0 },
                ]}
              >
                <Switch
                  value={isGoogleAuth}
                  testID="toggle-switch"
                  onValueChange={toggleSwitch}
                  trackColor={{ true: "#281C9D" }}
                  thumbColor={isGoogleAuth ? "#5655B9" : "lightgray"}
                  ios_backgroundColor="#281C9D"
                />
              </View>
              <View
                style={[
                  SignInStyle.imageContainer,
                  { flex: 2, justifyContent: "center" },
                ]}
              >
                <Image
                  source={require("../../../assets/images/login-page-image.png")}
                  style={SignInStyle.images}
                  resizeMode="contain"
                />
              </View>
              {isGoogleAuth ? (
                <View>
                  <AppText style={styles.label}>Email</AppText>
                  <AppTextInput
                    style={[styles.input]}
                    value={formData.email}
                    placeholder="Enter Email"
                    onChangeText={(value) => handleChange("email", value)}
                  />

                  <AppText style={styles.label}>Password</AppText>
                  <AppTextInput
                    style={styles.input}
                    value={formData.password}
                    onChangeText={(value) => handleChange("password", value)}
                    placeholder="Enter Password"
                    secureTextEntry={true}
                  />

                  <TouchableOpacity
                    style={commonStyles.button}
                    onPress={() => onLogin()}
                  >
                    <AppText style={commonStyles.buttonText}>Login</AppText>
                  </TouchableOpacity>
                </View>
              ) : (
                <View
                  style={[
                    { flex: 2, justifyContent: "center" },
                    Platform.OS === "web" ? { alignItems: "center" } : null,
                  ]}
                >
                  {Platform.OS !== "web" ? (
                    <View style={SignInStyle.containerBtn}>
                      <GoogleSigninButton
                        onPress={signIn}
                        size={GoogleSigninButton.Size.Wide}
                        color={GoogleSigninButton.Color.Dark}
                        style={SignInStyle.hiddenButton}
                      />
                      <TouchableOpacity
                        style={SignInStyle.signinContainer}
                        onPress={signIn}
                      >
                        <Image
                          style={SignInStyle.image}
                          source={require("../../../assets/images/google.png")}
                        />
                        <View style={SignInStyle.signButton}>
                          <OverflowTooltipText
                            text="Sign in with Google"
                            containerWidth="90%"
                            style={SignInStyle.signText}
                            onPress={signIn}
                          />
                        </View>
                      </TouchableOpacity>
                      <AppleAuth onLoginSuccess={handleAppleCredential} />
                    </View>
                  ) : (
                    <View>
                      <TouchableOpacity
                        style={SignInStyle.signinContainer}
                        onPress={() => promptAsync()}
                      >
                        <Image
                          style={SignInStyle.image}
                          source={require("../../../assets/images/google.png")}
                        />
                        <View style={SignInStyle.signButton}>
                          <AppText style={SignInStyle.signText}>
                            Sign in with Google
                          </AppText>
                        </View>
                      </TouchableOpacity>
                      <AppleAuth onLoginSuccess={handleAppleCredential} />
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>
          {/* <EmailPromptModal
            visible={emailModalVisible}
            initialEmail={pendingAppleData?.email || ""}
            onCancel={() => setEmailModalVisible(false)}
            onSubmit={handleEmailSubmit}
          /> */}
          <EmailPromptModal
            visible={emailModalVisible}
            initialEmail={pendingAppleData?.email || ""}
            initialFirstName={pendingAppleData?.firstName}
            initialLastName={pendingAppleData?.lastName}
            onCancel={() => setEmailModalVisible(false)}
            onSubmit={handleEmailSubmit}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
const styles = StyleSheet.create({
  label: {
    marginBottom: 8,
    fontSize: 12,
    color: "#979797",
    //fontWeight: "600",
    fontFamily: fonts.regular
  },
  input: {
    height: 45,
    borderWidth: 1,
    borderRadius: 15,
    paddingHorizontal: 12,
    fontSize: 14,
    color: "#343434",
    marginBottom: 24,
    //fontWeight: "600",
    borderColor: "#CBCBCB",
    fontFamily: fonts.regular},

  placeholderText: {
    fontSize: 14,
    color: "#979797",
    //fontWeight: "500",
    fontFamily: fonts.regular},

  errorBorder: {
    borderColor: "red"},
  errorText: {
    fontSize: 12,
    color: "red",
    marginBottom: 10,
    fontFamily: fonts.regular}});

export default SignInPage;
